import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { asistenciasApi, personasApi } from '../services/api'
import {
  Button,
  Badge,
  SearchInput,
  EmptyState,
  Modal,
  Tooltip,
  SkeletonRows,
  SkeletonCard,
  Tabs,
} from '../components/ui'
import EncabezadoPagina from '../components/ui/EncabezadoPagina'
import TarjetaCifra from '../components/ui/TarjetaCifra'
import { useScrollInfinito } from '../hooks/useScrollInfinito'
import AssistanceTypeCombobox from '../components/forms/AssistanceTypeCombobox'
import { useAssistanceTypes } from '../hooks/useAssistanceTypes'
import useAuthStore from '../stores/authStore'
import { cn } from '../lib/cn'

// ── Iconos locales ──────────────────────────────────────────
const I = {
  cards: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
  table: 'M3 10h18M3 14h18M3 6h18M3 18h18',
  download: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4',
  eye: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  eye2: 'M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
  edit: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  trash: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  filter: 'M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z',
  columns: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2',
  doc: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  paperclip: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
  chevL: 'M15 19l-7-7 7-7',
  chevR: 'M9 5l7 7-7 7',
  x: 'M6 18L18 6M6 6l12 12',
}
const Svg = ({ d, className = 'h-4 w-4', sw = 2 }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={sw} d={d} />
  </svg>
)

const isHospitalOrg = (org) => String(org || '').toLowerCase() === 'hospital'
const PAGE_SIZE = 18
const ICONO_PAGINA = 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z'
const ORIGENES = [
  { key: 'todos', label: 'Todos' },
  { key: 'accion_social_municipal', label: 'Acción Social' },
  { key: 'hospital', label: 'Hospital' },
  { key: 'caja_celiacos', label: 'Caja Celíacos' },
  { key: 'tarjeta_sapucay', label: 'Tarjeta Sapucay' },
  { key: 'tarjeta_mama_mbarete', label: 'Mamá Mbareté' },
  { key: 'viaje', label: 'Combi / Viajes' },
]
const iniciales = (apellido, nombre) => `${String(apellido || '?').trim()[0] || '?'}${String(nombre || '').trim()[0] || ''}`.toUpperCase()

const BeneficiariosList = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { loading: typesLoading, data: assistanceTypes, labelByValue } = useAssistanceTypes()
  // Etiquetas amigables para tipos especiales que no están en la config de tipos.
  const SPECIAL_TIPO_LABELS = { caja_celiacos: 'Caja Celíacos', viaje: 'Viaje en Combi', transito: 'Tránsito', tarjeta_sapucay: 'Tarjeta Sapucay', tarjeta_mama_mbarete: 'Tarjeta Mamá Mbareté' }
  const formatTipo = (val) => labelByValue?.get(val) || SPECIAL_TIPO_LABELS[val] || (val ? String(val).replace(/_/g, ' ') : '—')
  const [asistencias, setAsistencias] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  // La primera tanda reemplaza la lista y las siguientes se agregan al final. `pedidoId`
  // descarta respuestas viejas (cambió el filtro mientras venía una tanda).
  const pageRef = useRef(1)
  const pedidoId = useRef(0)
  const centinelaRef = useRef(null)

  // States para filtros y vista
  const [search, setSearch] = useState('')
  const [tipoAsistencia, setTipoAsistencia] = useState('')
  const [origen, setOrigen] = useState('') // '' | hospital | accion_social_municipal | caja_celiacos | viaje
  const [fechaDesde, setFechaDesde] = useState('')
  const [fechaHasta, setFechaHasta] = useState('')
  const [viewMode, setViewMode] = useState('card') // 'card' | 'table'
  const [showFilters, setShowFilters] = useState(false)

  const [selectedDoc, setSelectedDoc] = useState(null)
  const [beneficiaryToDelete, setBeneficiaryToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  // Estados para edición inline
  const [editingAsistencia, setEditingAsistencia] = useState(null)
  const [editForm, setEditForm] = useState({ fecha: '', detalle: '', tipo: '' })
  const [saving, setSaving] = useState(false)

  // Spreadsheet logic (APITable-inspired)
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'desc' })
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [visibleCols, setVisibleCols] = useState({ fecha: true, beneficiario: true, dni: true, barrio: true, expediente: true, organismo: true, cantMonto: true, motivo: true })
  const [showColMenu, setShowColMenu] = useState(false)
  const [expandedRow, setExpandedRow] = useState(null)

  const sortedAsistencias = useMemo(() => {
    let sortableItems = [...asistencias]
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let valA = a[sortConfig.key] || ''
        let valB = b[sortConfig.key] || ''
        if (sortConfig.key === 'beneficiario') {
          valA = `${a.persona_apellido} ${a.persona_nombre}`.toLowerCase()
          valB = `${b.persona_apellido} ${b.persona_nombre}`.toLowerCase()
        }
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortableItems
  }, [asistencias, sortConfig])

  // KPIs derivados de la página actual (sin endpoints nuevos)
  const kpis = useMemo(() => {
    const hospital = asistencias.filter((a) => isHospitalOrg(a.organismo)).length
    const social = asistencias.length - hospital
    const conExpediente = asistencias.filter((a) => a.nro_expediente).length
    return { hospital, social, conExpediente }
  }, [asistencias])

  const activeFilters = useMemo(() => {
    const list = []
    if (tipoAsistencia) list.push({ key: 'tipo', label: `Motivo: ${formatTipo(tipoAsistencia)}`, clear: () => setTipoAsistencia('') })
    if (fechaDesde) list.push({ key: 'desde', label: `Desde: ${fechaDesde}`, clear: () => setFechaDesde('') })
    if (fechaHasta) list.push({ key: 'hasta', label: `Hasta: ${fechaHasta}`, clear: () => setFechaHasta('') })
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoAsistencia, fechaDesde, fechaHasta])

  const requestSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc'
    setSortConfig({ key, direction })
  }

  const toggleSelectAll = useCallback(() => {
    if (selectedRows.size === sortedAsistencias.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(sortedAsistencias.map((a) => a.id)))
    }
  }, [sortedAsistencias, selectedRows])

  const toggleRow = useCallback((id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const colLabels = { fecha: 'Fecha', beneficiario: 'Beneficiario', dni: 'DNI', barrio: 'Barrio', expediente: 'Expediente', organismo: 'Organismo', cantMonto: 'Cant/Monto', motivo: 'Motivo' }

  const isAdmin = user?.rol === 'admin'

  // Recarga desde la primera tanda al cambiar un filtro (con espera en la búsqueda).
  useEffect(() => {
    const timer = setTimeout(() => { fetchData() }, search ? 400 : 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tipoAsistencia, origen, fechaDesde, fechaHasta, viewMode])

  // Los mismos filtros para listar y para exportar: el Excel trae lo que se está viendo.
  const filtrosActuales = () => {
    const isOrgOrigen = origen === 'hospital' || origen === 'accion_social_municipal'
    const isTipoOrigen = origen === 'caja_celiacos' || origen === 'viaje' || origen === 'tarjeta_sapucay' || origen === 'tarjeta_mama_mbarete'
    return {
      search,
      tipo: isTipoOrigen ? origen : tipoAsistencia,
      organismo: isOrgOrigen ? origen : undefined,
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
    }
  }

  const fetchData = async ({ append = false } = {}) => {
    const id = ++pedidoId.current
    const pagina = append ? pageRef.current + 1 : 1
    try {
      if (append) setLoadingMore(true); else setLoading(true)
      const isTable = viewMode === 'table'
      const res = await asistenciasApi.list({
        page: isTable ? 1 : pagina,
        limit: isTable ? 500 : PAGE_SIZE,
        ...filtrosActuales(),
      })
      if (id !== pedidoId.current) return
      const nuevas = res.data.data || []
      setAsistencias((prev) => {
        if (!append) return nuevas
        const vistos = new Set(prev.map((a) => a.id))
        return [...prev, ...nuevas.filter((a) => !vistos.has(a.id))]
      })
      pageRef.current = pagina
      setTotalRecords(res.data.pagination?.total ?? nuevas.length)
    } catch (error) {
      if (id !== pedidoId.current) return
      console.error(error)
      toast.error('Error al cargar la lista de beneficiarios')
    } finally {
      if (id === pedidoId.current) { setLoading(false); setLoadingMore(false) }
    }
  }

  const hayMas = viewMode === 'card' && asistencias.length < totalRecords
  const cargarMas = useCallback(() => {
    if (!loading && !loadingMore && hayMas) fetchData({ append: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadingMore, hayMas, viewMode, search, tipoAsistencia, origen, fechaDesde, fechaHasta])
  useScrollInfinito(centinelaRef, cargarMas, asistencias.length)

  const isImage = (url) => {
    if (!url) return false
    const lower = url.toLowerCase()
    return lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png') || lower.endsWith('.webp')
  }

  const openDeleteModal = (asistencia) => {
    setBeneficiaryToDelete({
      personaId: asistencia.persona_id,
      nombre: asistencia.persona_nombre,
      apellido: asistencia.persona_apellido,
      dni: asistencia.persona_dni,
    })
  }

  const handleDeleteBeneficiary = async () => {
    if (!beneficiaryToDelete?.personaId) return
    try {
      setDeleting(true)
      await personasApi.remove(beneficiaryToDelete.personaId)
      setAsistencias((current) => current.filter((item) => item.persona_id !== beneficiaryToDelete.personaId))
      setBeneficiaryToDelete(null)
      toast.success('Beneficiario eliminado correctamente')
      fetchData()
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.error || 'No se pudo eliminar el beneficiario')
    } finally {
      setDeleting(false)
    }
  }

  const handleViewBeneficiary = (personaId) => {
    if (!personaId) return
    navigate(`/persona/${personaId}`)
  }

  const handleEditOpen = (ast) => {
    setEditingAsistencia(ast)
    setEditForm({
      fecha: ast.fecha ? format(new Date(ast.fecha), 'yyyy-MM-dd') : '',
      detalle: ast.detalle || '',
      tipo: ast.tipo || '',
    })
  }

  const handleEditSave = async () => {
    if (!editingAsistencia) return
    try {
      setSaving(true)
      await asistenciasApi.update(editingAsistencia.id, {
        fecha: editForm.fecha || undefined,
        detalle: editForm.detalle || undefined,
        tipo: editForm.tipo || undefined,
      })
      toast.success('Asistencia actualizada correctamente')
      // Se corrige la fila en pantalla sin recargar: así no se pierde el lugar en la lista.
      const idEditado = editingAsistencia.id
      setAsistencias((cur) => cur.map((a) => (a.id !== idEditado ? a : {
        ...a,
        fecha: editForm.fecha ? new Date(`${editForm.fecha}T12:00:00`).toISOString() : a.fecha,
        detalle: editForm.detalle || a.detalle,
        tipo: editForm.tipo || a.tipo,
      })))
      setEditingAsistencia(null)
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.error || 'No se pudo actualizar la asistencia')
    } finally {
      setSaving(false)
    }
  }

  const handleExport = async () => {
    try {
      const loadingToast = toast.loading('Generando archivo Excel...')
      // xlsx pesa considerable: se carga sólo al exportar, no en el bundle inicial.
      const XLSX = await import('xlsx')
      const res = await asistenciasApi.list({ page: 1, limit: 10000, ...filtrosActuales() })
      const finalData = res.data.data
      const wsData = finalData.map((item) => ({
        DNI: item.persona_dni,
        Apellido: item.persona_apellido,
        Nombre: item.persona_nombre,
        Organismo: item.organismo,
        Fecha: format(new Date(item.fecha), 'dd/MM/yyyy'),
        'Motivo / Gestion': item.tipo,
        Detalle: item.detalle || '',
      }))
      const ws = XLSX.utils.json_to_sheet(wsData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Beneficiarios')
      XLSX.writeFile(wb, `Beneficiarios_${format(new Date(), 'dd-MM-yyyy')}.xlsx`)
      toast.dismiss(loadingToast)
      toast.success('Excel descargado correctamente')
    } catch (error) {
      toast.dismiss()
      console.error(error)
      toast.error('Error al generar Excel')
    }
  }

  const clearAllFilters = () => {
    setTipoAsistencia('')
    setFechaDesde('')
    setFechaHasta('')
  }

  const SortHead = ({ col, label, className }) => (
    <th
      onClick={() => requestSort(col)}
      className={cn('cursor-pointer select-none whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-soft hover:text-ink transition-colors', className)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortConfig.key === col && <span className="text-social-700">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  )

  const hayFiltros = Boolean(search.trim() || tipoAsistencia || origen || fechaDesde || fechaHasta)

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <EncabezadoPagina
        icono={<Svg d={ICONO_PAGINA} className="h-full w-full" />}
        eyebrow="Acción Social · Padrón municipal unificado"
        titulo={isAdmin ? 'Beneficiarios' : 'Mis beneficiarios'}
        descripcion={isAdmin
          ? 'Historial de asistencias y documentación respaldatoria.'
          : 'Beneficiarios que registraste. Podés editar fecha, detalle y motivo.'}
        acciones={(
          <Tooltip content="Exportar a Excel lo que se está viendo">
            <Button variant="social" onClick={handleExport} aria-label="Exportar a Excel" iconLeft={<Svg d={I.download} className="h-4 w-4" />}>
              Exportar Excel
            </Button>
          </Tooltip>
        )}
      />

      {/* ── Cifras: el total sigue a los filtros; el desglose cuenta lo que hay en pantalla ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-live="polite">
        <TarjetaCifra titulo="Registros" valor={totalRecords} tono="social" cargando={loading && asistencias.length === 0}
          sub={hayFiltros ? 'con los filtros puestos' : 'en el padrón'} />
        <TarjetaCifra titulo="Acción Social" valor={kpis.social} tono="emerald" sub={`de ${asistencias.length} en pantalla`} />
        <TarjetaCifra titulo="Hospital" valor={kpis.hospital} tono="sky" sub={`de ${asistencias.length} en pantalla`} />
        <TarjetaCifra titulo="Con expediente" valor={kpis.conExpediente} tono="amber" sub={`de ${asistencias.length} en pantalla`} />
      </div>

      {/* ── Buscar, vista y filtros ── */}
      <section aria-label="Buscar y filtrar" className="space-y-4 rounded-2xl border border-hairline bg-white p-4 shadow-card sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <SearchInput
            value={search}
            onChange={setSearch}
            label="Buscar beneficiario"
            placeholder="DNI, apellido o expediente…"
            className="w-full lg:max-w-md"
          />
          <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
            <Tabs
              segmented
              value={viewMode}
              onChange={setViewMode}
              aria-label="Modo de vista"
              tabs={[
                { key: 'card', label: 'Tarjetas', icon: <Svg d={I.cards} className="h-4 w-4" /> },
                { key: 'table', label: 'Planilla', icon: <Svg d={I.table} className="h-4 w-4" /> },
              ]}
            />
            <Button variant="outline" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters} iconLeft={<Svg d={I.filter} className="h-4 w-4" />}>
              Filtros{activeFilters.length > 0 && <Badge tone="info" className="ml-1">{activeFilters.length}</Badge>}
            </Button>
          </div>
        </div>

        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-ink-muted">Origen de la asistencia</p>
          <Tabs
            buttons
            accent="social"
            value={origen || 'todos'}
            onChange={(k) => setOrigen(k === 'todos' ? '' : k)}
            tabs={ORIGENES}
            aria-label="Origen de la asistencia"
          />
        </div>

        {showFilters && (
          <div className="animate-fade-in border-t border-hairline pt-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="label">Motivo / Gestión</label>
                <AssistanceTypeCombobox
                  value={tipoAsistencia}
                  onChange={setTipoAsistencia}
                  categories={assistanceTypes.categories}
                  loading={typesLoading}
                  includeAll
                  allValue=""
                  allLabel="Todos los motivos"
                  showRecents={false}
                  placeholder="Filtrar por motivo…"
                />
              </div>
              <div>
                <label className="label" htmlFor="f-desde">Fecha desde</label>
                <input id="f-desde" type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="input" />
              </div>
              <div>
                <label className="label" htmlFor="f-hasta">Fecha hasta</label>
                <input id="f-hasta" type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="input" />
              </div>
            </div>
            {activeFilters.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
                <span className="text-xs font-medium text-ink-soft">Activos:</span>
                {activeFilters.map((f) => (
                  <button key={f.key} onClick={f.clear} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-ink-soft hover:bg-slate-200">
                    {f.label}
                    <Svg d={I.x} className="h-3 w-3" />
                  </button>
                ))}
                <button onClick={clearAllFilters} className="text-xs font-semibold text-social-700 hover:underline">Limpiar todo</button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Contenido ── */}
      {loading && asistencias.length > 0 && <p className="text-xs font-semibold text-ink-muted" role="status">Actualizando…</p>}
      {loading && asistencias.length === 0 ? (
        viewMode === 'table' ? (
          <div className="table-container bg-surface">
            <table className="data-table">
              <tbody><SkeletonRows rows={8} cols={6} /></tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )
      ) : asistencias.length === 0 ? (
        <EmptyState
          icon={<Svg d={I.doc} className="h-8 w-8" />}
          title="No se encontraron resultados"
          description="Probá con otro DNI o apellido, o verificá que las asistencias ya estén registradas."
        />
      ) : viewMode === 'table' ? (
        /* ── Vista planilla ── */
        <div className="flex max-h-[78vh] flex-col overflow-hidden rounded-2xl border border-hairline bg-surface shadow-card">
          {totalRecords > asistencias.length && (
            <p className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs font-semibold text-amber-900">
              Se muestran los primeros {asistencias.length} de {totalRecords}. Para ver el resto usá los filtros, o exportá todo a Excel.
            </p>
          )}
          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-hairline px-5 py-3">
            <div className="flex items-center gap-2.5">
              {selectedRows.size > 0 ? (
                <Badge tone="info">{selectedRows.size} seleccionados</Badge>
              ) : (
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Padrón municipal</span>
              )}
              <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-soft">{sortedAsistencias.length} filas</span>
            </div>
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setShowColMenu(!showColMenu)} iconLeft={<Svg d={I.columns} className="h-4 w-4" />}>
                Columnas
              </Button>
              {showColMenu && (
                <div className="absolute right-0 top-full z-50 mt-2 w-52 rounded-xl border border-hairline bg-surface p-2 shadow-card-hover animate-fade-in">
                  <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-ink-muted">Mostrar columnas</p>
                  {Object.entries(colLabels).map(([key, label]) => (
                    <label key={key} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium text-ink hover:bg-slate-50">
                      <input type="checkbox" checked={visibleCols[key]} onChange={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))} className="rounded border-slate-300 text-social-700 focus:ring-social-700" />
                      {label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-auto" onClick={() => showColMenu && setShowColMenu(false)}>
            <table className="w-full border-collapse text-left text-sm text-ink">
              <thead className="sticky top-0 z-20 bg-slate-50 shadow-[0_1px_0_theme(colors.hairline)]">
                <tr>
                  <th className="sticky left-0 z-30 w-12 border-b border-r border-hairline bg-slate-100 px-3 py-3">
                    <input type="checkbox" aria-label="Seleccionar todo" checked={selectedRows.size === sortedAsistencias.length && sortedAsistencias.length > 0} onChange={toggleSelectAll} className="rounded border-slate-300 text-social-700 focus:ring-social-700" />
                  </th>
                  {visibleCols.fecha && <SortHead col="fecha" label="Fecha" className="border-b border-r border-hairline" />}
                  {visibleCols.beneficiario && <SortHead col="beneficiario" label="Beneficiario" className="min-w-[200px] border-b border-r border-hairline" />}
                  {visibleCols.dni && <SortHead col="persona_dni" label="DNI" className="border-b border-r border-hairline" />}
                  {visibleCols.barrio && <th className="border-b border-r border-hairline px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-soft">Barrio</th>}
                  {visibleCols.expediente && <SortHead col="nro_expediente" label="Expediente" className="border-b border-r border-hairline" />}
                  {visibleCols.organismo && <th className="border-b border-r border-hairline px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-soft">Organismo</th>}
                  {visibleCols.cantMonto && <th className="border-b border-r border-hairline px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-soft">Cant / Monto</th>}
                  {visibleCols.motivo && <th className="min-w-[200px] border-b border-r border-hairline px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-soft">Motivo</th>}
                  <th className="sticky right-0 z-30 border-b border-hairline bg-slate-100 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-ink-soft">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedAsistencias.map((ast, idx) => {
                  const isSelected = selectedRows.has(ast.id)
                  const isExpanded = expandedRow === ast.id
                  const hosp = isHospitalOrg(ast.organismo)
                  return (
                    <React.Fragment key={ast.id}>
                      <tr className={cn('border-b border-hairline transition-colors', isSelected ? 'bg-hospital-50/60' : idx % 2 === 0 ? 'bg-surface' : 'bg-slate-50/40', 'hover:bg-social-700/5')}>
                        <td className="sticky left-0 z-10 border-r border-hairline bg-inherit px-3 py-3">
                          <input type="checkbox" aria-label={`Seleccionar fila ${idx + 1}`} checked={isSelected} onChange={() => toggleRow(ast.id)} className="rounded border-slate-300 text-social-700 focus:ring-social-700" />
                        </td>
                        {visibleCols.fecha && <td className="whitespace-nowrap border-r border-hairline px-4 py-3 font-medium text-ink-soft">{format(new Date(ast.fecha), 'dd/MM/yy')}</td>}
                        {visibleCols.beneficiario && (
                          <td className="border-r border-hairline px-4 py-3">
                            <button onClick={() => handleViewBeneficiary(ast.persona_id)} className="text-left font-semibold text-ink hover:text-social-700 hover:underline">
                              {ast.persona_apellido}, {ast.persona_nombre}
                            </button>
                          </td>
                        )}
                        {visibleCols.dni && <td className="border-r border-hairline px-4 py-3 font-mono text-[13px] text-ink-soft">{ast.persona_dni}</td>}
                        {visibleCols.barrio && <td className="border-r border-hairline px-4 py-3 text-ink-soft">{ast.persona_barrio || <span className="text-ink-muted">—</span>}</td>}
                        {visibleCols.expediente && (
                          <td className="border-r border-hairline px-4 py-3">
                            {ast.nro_expediente ? <span className="rounded-md border border-hairline bg-slate-50 px-2 py-0.5 font-mono text-xs text-ink-soft">{ast.nro_expediente}</span> : <span className="text-ink-muted">—</span>}
                          </td>
                        )}
                        {visibleCols.organismo && (
                          <td className="border-r border-hairline px-4 py-3">
                            <Badge tone={hosp ? 'hospital' : 'social'}>{ast.organismo?.replace(/_/g, ' ')}</Badge>
                          </td>
                        )}
                        {visibleCols.cantMonto && (
                          <td className="border-r border-hairline px-4 py-3">
                            {ast.cantidad || ast.monto ? (
                              <div className="flex items-center gap-2">
                                {ast.cantidad && <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-ink-soft">×{ast.cantidad}</span>}
                                {ast.monto && <span className="text-sm font-semibold text-success-dark">${Number(ast.monto).toLocaleString('es-AR')}</span>}
                              </div>
                            ) : <span className="text-ink-muted">—</span>}
                          </td>
                        )}
                        {visibleCols.motivo && (
                          <td className="border-r border-hairline px-4 py-3">
                            <button className="line-clamp-1 max-w-[200px] text-left font-medium text-ink hover:text-social-700" title={formatTipo(ast.tipo)} onClick={() => setExpandedRow(isExpanded ? null : ast.id)}>
                              {formatTipo(ast.tipo)}
                            </button>
                          </td>
                        )}
                        <td className="sticky right-0 z-10 border-l border-hairline bg-inherit px-3 py-3">
                          <div className="flex justify-center gap-1">
                            <Tooltip content="Ver ficha"><button onClick={() => handleViewBeneficiary(ast.persona_id)} aria-label="Ver ficha completa" className="rounded-lg p-1.5 text-social-700 hover:bg-social-700/10"><Svg d={I.eye2} className="h-4 w-4" /></button></Tooltip>
                            <Tooltip content="Editar"><button onClick={() => handleEditOpen(ast)} aria-label="Editar asistencia" className="rounded-lg p-1.5 text-warning-dark hover:bg-warning-soft"><Svg d={I.edit} className="h-4 w-4" /></button></Tooltip>
                            {isAdmin && <Tooltip content="Eliminar"><button onClick={() => openDeleteModal(ast)} aria-label="Eliminar beneficiario" className="rounded-lg p-1.5 text-danger hover:bg-danger-soft"><Svg d={I.trash} className="h-4 w-4" /></button></Tooltip>}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-hairline bg-social-700/5">
                          <td colSpan={99} className="px-6 py-4">
                            <div className="grid grid-cols-1 gap-5 rounded-xl border border-hairline bg-surface p-4 md:grid-cols-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Motivo</p>
                                <p className="mt-1 font-semibold text-ink">{formatTipo(ast.tipo)}</p>
                              </div>
                              {ast.detalle && (
                                <div className="md:col-span-2">
                                  <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Detalle</p>
                                  <p className="mt-1 italic text-ink-soft">“{ast.detalle}”</p>
                                </div>
                              )}
                              {ast.creado_por_nombre && (
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Registrado por</p>
                                  <p className="mt-1 font-medium text-ink-soft">@{ast.creado_por_nombre}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-shrink-0 items-center justify-between border-t border-hairline bg-slate-50 px-5 py-3 text-xs font-medium text-ink-soft">
            <span>{sortedAsistencias.length} registros · {selectedRows.size > 0 ? `${selectedRows.size} seleccionados` : 'ninguno seleccionado'}</span>
            <span className="hidden text-ink-muted sm:inline">Clic en cabeceras para ordenar</span>
          </div>
        </div>
      ) : (
        /* ── Vista tarjetas ── */
        <div className={cn('grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3', loading && 'opacity-60 transition-opacity')}>
          {asistencias.map((ast) => {
            const hosp = isHospitalOrg(ast.organismo)
            const fotos = ast.fotos || []
            return (
              <div key={ast.id} className="group relative flex flex-col overflow-hidden rounded-2xl border border-hairline bg-surface p-5 shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card-hover">
                <div className={cn('absolute left-0 top-0 h-full w-1', hosp ? 'bg-hospital-600' : 'bg-social-600')} />
                <div className="flex items-start justify-between gap-2">
                  <Badge tone={hosp ? 'hospital' : 'social'}>{ast.organismo?.replace(/_/g, ' ')}</Badge>
                  <span className="text-xs font-medium text-ink-muted">{format(new Date(ast.fecha), 'dd/MM/yy', { locale: es })}</span>
                </div>

                <button type="button" onClick={() => handleViewBeneficiary(ast.persona_id)} className="mt-3 flex items-center gap-3 text-left">
                  <span aria-hidden="true" className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-black', hosp ? 'bg-hospital-50 text-hospital-700' : 'bg-social-100 text-social-800')}>
                    {iniciales(ast.persona_apellido, ast.persona_nombre)}
                  </span>
                  <h3 className="line-clamp-2 text-lg font-bold leading-tight text-ink transition-colors group-hover:text-social-700">
                    {ast.persona_apellido}, {ast.persona_nombre}
                  </h3>
                </button>
                <p className="mt-1 text-xs text-ink-soft">
                  <span className="font-mono font-medium">DNI {ast.persona_dni}</span>
                  {ast.persona_barrio && <span> · {ast.persona_barrio}</span>}
                  {ast.nro_expediente && <span> · Exp. {ast.nro_expediente}</span>}
                </p>

                <div className="mt-3 rounded-xl border border-hairline bg-slate-50/70 p-3">
                  <p className="font-semibold leading-snug text-ink">{formatTipo(ast.tipo)}</p>
                  {(ast.cantidad || ast.monto) && (
                    <p className="mt-1 text-xs font-semibold text-ink-soft">
                      {ast.cantidad && <span>×{ast.cantidad}</span>}
                      {ast.cantidad && ast.monto && <span> · </span>}
                      {ast.monto && <span className="text-success-dark">${Number(ast.monto).toLocaleString('es-AR')}</span>}
                    </p>
                  )}
                  {ast.detalle && <p className="mt-2 line-clamp-3 border-l-2 border-hairline pl-3 text-sm italic text-ink-soft" title={ast.detalle}>“{ast.detalle}”</p>}
                </div>

                {fotos.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-ink-muted"><Svg d={I.paperclip} className="h-3.5 w-3.5" /> {fotos.length}</span>
                    {fotos.map((f, index) => (
                      <span key={index} className="inline-flex items-center overflow-hidden rounded-lg border border-hairline text-xs font-semibold">
                        <a href={f.url} download target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-ink-soft transition-colors hover:bg-social-700 hover:text-white">
                          Doc {index + 1} <Svg d={I.download} className="h-3 w-3" />
                        </a>
                        {isImage(f.url) && (
                          <button type="button" onClick={() => setSelectedDoc(f.url)} className="border-l border-hairline px-2 py-1 text-social-700 hover:bg-social-700/10">Ver</button>
                        )}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2 border-t border-hairline pt-3">
                  <Button variant="social" size="sm" fullWidth onClick={() => handleViewBeneficiary(ast.persona_id)} iconRight={<Svg d={I.chevR} className="h-3.5 w-3.5" sw={3} />}>
                    Ver ficha
                  </Button>
                  <Tooltip content="Editar asistencia">
                    <Button variant="outline" size="sm" onClick={() => handleEditOpen(ast)} aria-label="Editar asistencia" iconLeft={<Svg d={I.edit} className="h-4 w-4" />} />
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip content="Eliminar beneficiario">
                      <Button variant="outline" size="sm" onClick={() => openDeleteModal(ast)} aria-label="Eliminar beneficiario" className="text-danger hover:bg-danger-soft" iconLeft={<Svg d={I.trash} className="h-4 w-4" />} />
                    </Tooltip>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Scroll continuo (solo tarjetas): al llegar al pie trae la tanda siguiente ── */}
      {viewMode === 'card' && asistencias.length > 0 && (
        <div ref={centinelaRef} className="py-4 text-center text-sm font-semibold text-ink-muted" aria-live="polite">
          {loadingMore ? (
            'Cargando más…'
          ) : hayMas ? (
            <Button variant="outline" onClick={cargarMas}>Cargar más ({asistencias.length} de {totalRecords})</Button>
          ) : (
            `Ya viste los ${totalRecords.toLocaleString('es-AR')} registros`
          )}
        </div>
      )}

      {/* ── Modal eliminar ── */}
      <Modal
        isOpen={Boolean(beneficiaryToDelete)}
        onClose={() => !deleting && setBeneficiaryToDelete(null)}
        title="Eliminar beneficiario"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setBeneficiaryToDelete(null)} disabled={deleting}>Cancelar</Button>
            <Button variant="danger" onClick={handleDeleteBeneficiary} loading={deleting}>{deleting ? 'Eliminando…' : 'Confirmar'}</Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">
            Se eliminará <strong className="text-ink">{beneficiaryToDelete?.apellido}, {beneficiaryToDelete?.nombre}</strong> y su historial asociado.
          </p>
          <div className="rounded-xl border border-danger/20 bg-danger-soft px-4 py-3 text-sm text-danger-dark">
            DNI: <strong>{beneficiaryToDelete?.dni || 'Sin dato'}</strong>. Esta acción impacta asistencias y vínculos del registro y no se puede deshacer.
          </div>
        </div>
      </Modal>

      {/* ── Modal editar ── */}
      <Modal
        isOpen={Boolean(editingAsistencia)}
        onClose={() => !saving && setEditingAsistencia(null)}
        title="Editar asistencia"
        description={editingAsistencia ? `${editingAsistencia.persona_apellido}, ${editingAsistencia.persona_nombre}` : undefined}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditingAsistencia(null)} disabled={saving}>Cancelar</Button>
            <Button variant="social" onClick={handleEditSave} loading={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Motivo / Gestión</label>
            <AssistanceTypeCombobox
              value={editForm.tipo}
              onChange={(tipo) => setEditForm((prev) => ({ ...prev, tipo }))}
              categories={assistanceTypes.categories}
              loading={typesLoading}
              placeholder="Buscar tipo…"
            />
          </div>
          <div>
            <label className="label" htmlFor="edit-fecha">Fecha</label>
            <input id="edit-fecha" type="date" value={editForm.fecha} onChange={(e) => setEditForm((prev) => ({ ...prev, fecha: e.target.value }))} className="input" />
          </div>
          <div>
            <label className="label" htmlFor="edit-detalle">Detalle / Observación</label>
            <textarea id="edit-detalle" value={editForm.detalle} onChange={(e) => setEditForm((prev) => ({ ...prev, detalle: e.target.value }))} rows={3} className="input resize-none" placeholder="Detalle de la asistencia…" />
          </div>
          <div className="flex items-start gap-2 rounded-xl border border-warning/20 bg-warning-soft px-4 py-3 text-sm text-warning-dark">
            <Svg d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="mt-0.5 h-4 w-4 shrink-0" />
            Solo puede editar asistencias que usted registró. Los cambios quedan en auditoría.
          </div>
        </div>
      </Modal>

      {/* ── Lightbox documento ── */}
      {selectedDoc && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-ink/95 p-4 backdrop-blur-md animate-fade-in" onClick={() => setSelectedDoc(null)}>
          <button onClick={() => setSelectedDoc(null)} aria-label="Cerrar" className="absolute right-4 top-4 rounded-full p-2 text-white hover:bg-white/10">
            <Svg d={I.x} className="h-8 w-8" sw={2.5} />
          </button>
          <div className="relative h-[80vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-surface" onClick={(e) => e.stopPropagation()}>
            <img src={selectedDoc} alt="Documento adjunto" className="h-full w-full object-contain" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink/60 to-transparent p-5">
              <a href={selectedDoc} download className="btn-primary inline-flex">
                <Svg d={I.download} className="h-4 w-4" /> Descargar original
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BeneficiariosList
